import {useMemo, useState} from "react";
import {Button} from "primereact/button";
import {confirmDialog} from "primereact/confirmdialog";
import {useDispatch} from "react-redux";
import {DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter} from "@dnd-kit/core";
import {restrictToParentElement} from "@dnd-kit/modifiers";
import {SortableContext, useSortable, arrayMove, verticalListSortingStrategy, sortableKeyboardCoordinates} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {useTranslate} from "../../system/useTranslate";
import {setMessage} from "../../system/app-slice";
import Icon from "../../system/components/icon";
import PostCategoryDialog from "./post-category-dialog";

export const POST_CATEGORIES_QUERY = {
    sort: "order:ASC",
    populate: "parent",
};

function relationKey(value) {
    return String(value?.documentId ?? value?.id ?? "");
}

function SortableCategoryRow({category, onEdit, onDelete}) {
    const translate = useTranslate();
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: relationKey(category)});
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 50 : undefined,
        opacity: isDragging ? 0.85 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="flex align-items-center justify-content-between gap-2 w-full border-1 border-300 border-round p-2 cursor-grab surface-0"
        >
            <div className="flex align-items-center gap-2 text-xs overflow-hidden">
                <Icon icon={`mi mi:drag_indicator`} className="text-lg text-400"/>
                {category.icon && <Icon icon={`mi mi:${category.icon}`} className="text-xl text-primary"/>}
                <div className="flex flex-column overflow-hidden">
                    {category.parent?.title && (
                        <div className="flex align-items-center gap-1 text-2xs text-400">
                            <Icon icon="mi mi:arrow_top_left" className="text-sm text-400"/>
                            <span className="whitespace-nowrap overflow-hidden text-overflow-ellipsis">{category.parent.title}</span>
                        </div>
                    )}
                    <span className="font-bold whitespace-nowrap overflow-hidden text-overflow-ellipsis">{category.title}</span>
                </div>
                <span className={`text-2xs border-1 border-round p-1 ${category.publicationStatus === "published" ? "border-green-500 text-green-500" : "border-yellow-500 text-yellow-500"}`}>
                    {translate(category.publicationStatus)}
                </span>
            </div>
            <div className="flex gap-2 align-items-center">
                <Button
                    type="button"
                    onClick={() => onEdit(category)}
                    className="p-button-rounded p-button-text p-button-info w-2rem h-2rem text-xs"
                    icon={<Icon icon="mi mi:edit" className="text-lg"/>}
                    aria-label={translate("modify-post-category")}
                />
                <Button
                    type="button"
                    onClick={() => onDelete(category)}
                    className="p-button-rounded p-button-text p-button-danger w-2rem h-2rem text-xs"
                    icon={<Icon icon="mi mi:delete" className="text-lg"/>}
                    aria-label={translate("delete-post-category")}
                />
            </div>
        </div>
    );
}

function PostCategoryManager({categoriesCollection}) {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editing, setEditing] = useState(null);

    const items = useMemo(() => {
        if (!categoriesCollection?.items?.length) return [];
        return [...categoriesCollection.items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }, [categoriesCollection?.items]);

    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}),
    );

    const notify = (severity, key) => dispatch(setMessage({severity, content: translate(key)}));

    const nextOrder = () => (items.length ? Math.max(...items.map((item) => item.order ?? 0)) + 1 : 1);

    const onDragEnd = ({active, over}) => {
        if (!over || active.id === over.id) return;
        const oldIndex = items.findIndex((item) => relationKey(item) === active.id);
        const newIndex = items.findIndex((item) => relationKey(item) === over.id);
        if (oldIndex < 0 || newIndex < 0) return;

        const next = arrayMove(items, oldIndex, newIndex);
        const updates = next
            .map((item, index) => (item.order !== index + 1 ? {item, order: index + 1} : null))
            .filter(Boolean);

        if (!updates.length) return;

        (async () => {
            for (const {item, order} of updates) {
                try {
                    await categoriesCollection.update(item.documentId, {order});
                } catch (error) {
                    notify("error", "post-category-order-failed");
                    await categoriesCollection.reload(POST_CATEGORIES_QUERY, {all: true}).catch(() => undefined);
                    return;
                }
            }
            notify("success", "post-category-order-saved");
            await categoriesCollection.reload(POST_CATEGORIES_QUERY, {all: true}).catch(() => undefined);
        })();
    };

    const confirmDelete = (category) => {
        confirmDialog({
            message: translate("delete-post-category-message").replace("{{CATEGORY_TITLE}}", category.title),
            header: translate("delete-post-category"),
            icon: "pi pi-exclamation-triangle",
            defaultFocus: "reject",
            acceptLabel: translate("delete"),
            rejectLabel: translate("cancel"),
            accept: async () => {
                try {
                    await categoriesCollection.remove(category.documentId);
                    notify("success", "post-category-deleted");
                    await categoriesCollection.reload(POST_CATEGORIES_QUERY, {all: true}).catch(() => undefined);
                } catch (error) {
                    notify("error", "post-category-delete-failed");
                }
            },
        });
    };

    const openCreate = () => {
        setEditing(null);
        setDialogVisible(true);
    };

    const openEdit = (category) => {
        setEditing(category);
        setDialogVisible(true);
    };

    return (
        <div className="w-full flex flex-column gap-2">
            <div className="flex align-items-start justify-content-between gap-3">
                <div className="flex flex-column gap-1 min-w-0">
                    <span className="font-bold text-xs">{translate("post-categories")}</span>
                    <span className="text-xs text-400">{translate("post-categories-sort-hint")}</span>
                </div>
                <Button
                    type="button"
                    label={translate("create-post-category")}
                    icon={<Icon icon="mi mi:add" className="text-lg"/>}
                    className="p-button-outlined p-2 text-xs gap-2 flex-none whitespace-nowrap"
                    onClick={openCreate}
                />
            </div>

            {items.length === 0 ? (
                <span className="text-xs text-400 border-round surface-50 p-3">{translate("no-post-category")}</span>
            ) : (
                <div className="w-full flex flex-column gap-2">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        modifiers={[restrictToParentElement]}
                        onDragEnd={onDragEnd}
                    >
                        <SortableContext items={items.map((item) => relationKey(item))} strategy={verticalListSortingStrategy}>
                            {items.map((category) => (
                                <SortableCategoryRow
                                    key={relationKey(category)}
                                    category={category}
                                    onEdit={openEdit}
                                    onDelete={confirmDelete}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                </div>
            )}

<PostCategoryDialog
                visible={dialogVisible}
                setVisible={setDialogVisible}
                collection={categoriesCollection}
                category={editing}
                order={nextOrder()}
                onSaved={() => categoriesCollection.reload(POST_CATEGORIES_QUERY, {all: true}).catch(() => undefined)}
            />
        </div>
    );
}

export default PostCategoryManager;